import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import {
  baixarParcelaDoVale,
  createEmployeeValesApi,
  getParcelaDoVale,
  listParcelasDoVale,
  type BaixarParcelaInput,
} from './api';
import type {
  EmployeeValeDto,
  EmployeeValeInput,
  EmployeeValeListItem,
  EmployeeValeParcelaDto,
} from './types';

export function useEmployeeValesHooks(employeeId: number): CrudHooks<
  EmployeeValeListItem,
  EmployeeValeDto,
  EmployeeValeInput,
  number
> & {
  api: CrudApi<EmployeeValeListItem, EmployeeValeDto, EmployeeValeInput, number>;
  useParcelas: (
    valeCodigo: number | null,
  ) => UseQueryResult<ListEnvelope<EmployeeValeParcelaDto>, Error>;
  useParcelaDetail: (
    valeCodigo: number | null,
    parcelaCodigo: number | null,
  ) => UseQueryResult<EmployeeValeParcelaDto, Error>;
  useBaixarParcela: () => UseMutationResult<
    EmployeeValeParcelaDto,
    Error,
    { valeCodigo: number; parcelaCodigo: number; input: BaixarParcelaInput }
  >;
} {
  const valesApi = useMemo(() => createEmployeeValesApi(employeeId), [employeeId]);
  const queryKey = useMemo(() => ['employees', employeeId, 'vales'] as const, [employeeId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<EmployeeValeListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => valesApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (id: number | null | undefined): UseQueryResult<EmployeeValeDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', id],
      queryFn: () => valesApi.get(id as number),
      enabled: id !== null && id !== undefined,
    });

  const useCreate = (): UseMutationResult<EmployeeValeDto, Error, EmployeeValeInput> =>
    useMutation({
      mutationFn: (input) => valesApi.create(input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });

  const useUpdate = (): UseMutationResult<
    EmployeeValeDto,
    Error,
    { id: number; input: EmployeeValeInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => valesApi.update(id, input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });

  const useDelete = (): UseMutationResult<void, Error, number> =>
    useMutation({
      mutationFn: (id) => valesApi.remove(id),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
      },
    });

  const useParcelas = (
    valeCodigo: number | null,
  ): UseQueryResult<ListEnvelope<EmployeeValeParcelaDto>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'parcelas', valeCodigo],
      queryFn: () => listParcelasDoVale(employeeId, valeCodigo as number),
      enabled: valeCodigo !== null,
    });

  const useParcelaDetail = (
    valeCodigo: number | null,
    parcelaCodigo: number | null,
  ): UseQueryResult<EmployeeValeParcelaDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'parcela', valeCodigo, parcelaCodigo],
      queryFn: () => getParcelaDoVale(employeeId, valeCodigo as number, parcelaCodigo as number),
      enabled: valeCodigo !== null && parcelaCodigo !== null,
    });

  // Baixa de parcela (Fase 2 Pacote A — card [h7TE7xcf]).
  // Invalida tanto a list de vales (header pode transitar pra PAG ao baixar a
  // última parcela) quanto a list de parcelas do vale específico.
  const useBaixarParcela = (): UseMutationResult<
    EmployeeValeParcelaDto,
    Error,
    { valeCodigo: number; parcelaCodigo: number; input: BaixarParcelaInput }
  > =>
    useMutation({
      mutationFn: ({ valeCodigo, parcelaCodigo, input }) =>
        baixarParcelaDoVale(employeeId, valeCodigo, parcelaCodigo, input),
      onSuccess: (_data, { valeCodigo }) => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: [...queryKey, 'parcelas', valeCodigo] });
      },
    });

  return {
    api: valesApi,
    useList,
    useDetail,
    useCreate,
    useUpdate,
    useDelete,
    useParcelas,
    useParcelaDetail,
    useBaixarParcela,
  };
}
